import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { uploadFile, fileUriToSrc } from '../../../api/files';
import { getConfig } from '../../../config';
import type { FileSectionLimits } from '../../../config';
import { parseFileSizeToBytes } from './parseFileSize';

export interface UploadedFile {
  id: string;
  uri: string;
  filename?: string;
  size?: number;
  /** Подпись второй строки (тип документа и т.п.), как description в FileLoaded web-client. */
  description?: string;
}

export interface FileUploadProps {
  section: string;
  value?: UploadedFile | null;
  onChange?: (file: UploadedFile) => void;
  disabled?: boolean;
  /** Для изображений — показывать превью. Для документов — иконку и имя. */
  showPreview?: boolean;
  /** id для input (для aria-describedby от родительского label) */
  id?: string;
  /** Ошибка валидации с сервера (422): подсветка зоны выбора и текст под полем */
  serverError?: string | null;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label<{ $disabled: boolean; $error?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border: 1px
    ${({ $error }) => ($error ? 'solid' : 'dashed')}
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.text.disabled)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  font-size: 14px;
  color: ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.text.secondary)};
  transition: border-color 0.2s;

  &:hover:not([data-disabled]) {
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  input {
    display: none;
  }
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.background};
`;

const PreviewImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
`;

const PreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const ServerErrorText = styled(ErrorText)`
  display: block;
  margin-top: 4px;
`;

function getSectionLimits(section: string): FileSectionLimits | undefined {
  try {
    const config = getConfig();

    return config.fileSections?.[section];
  } catch {
    return undefined;
  }
}

function validateFile(
  file: File,
  limits: FileSectionLimits | undefined,
): string | null {
  if (!limits) {
    return null;
  }
  const maxBytes = parseFileSizeToBytes(limits.maxFileSize);
  if (maxBytes > 0 && file.size > maxBytes) {
    return `Размер файла превышает допустимый (${limits.maxFileSize})`;
  }
  if (limits.allowedExtensions && limits.allowedExtensions.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (
      !limits.allowedExtensions.some(
        (e) => e.toLowerCase().replace(/^\./, '') === ext,
      )
    ) {
      return `Допустимые форматы: ${limits.allowedExtensions.join(', ')}`;
    }
  }

  return null;
}

export function FileUpload({
  section,
  value,
  onChange,
  disabled = false,
  showPreview = true,
  id,
  serverError = null,
}: FileUploadProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [internalFile, setInternalFile] = useState<UploadedFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const limits = getSectionLimits(section);

  const displayFile = value !== undefined ? value : internalFile;

  const clearPreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return null;
    });
  }, []);

  useEffect(() => {
    return clearPreview;
  }, [clearPreview]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      e.target.value = '';

      const validationError = validateFile(file, limits);
      if (validationError) {
        setError(validationError);

        return;
      }
      setError(null);
      setLoading(true);
      clearPreview();

      if (file.type.startsWith('image/') && showPreview) {
        setPreviewUrl(URL.createObjectURL(file));
      }

      try {
        const result = await uploadFile(section, file);
        setInternalFile(result);
        onChange?.(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Ошибка при загрузке файла',
        );
      } finally {
        setLoading(false);
      }
    },
    [section, onChange, limits, showPreview, clearPreview],
  );

  const isImage =
    displayFile?.filename != null
      ? /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(displayFile.filename)
      : false;

  const imageSrc =
    displayFile && isImage ? previewUrl ?? fileUriToSrc(displayFile.uri) : null;

  const hasServerError = Boolean(serverError?.trim());
  const showClientError = Boolean(error) && !hasServerError;
  const borderError = hasServerError || Boolean(error);
  const serverErrorId = id && hasServerError ? `${id}-server-error` : undefined;

  return (
    <Wrapper>
      <InputLabel
        $disabled={disabled || loading}
        $error={borderError}
        data-disabled={disabled || loading ? true : undefined}
      >
        <input
          id={id}
          ref={inputRef}
          type="file"
          disabled={disabled || loading}
          onChange={handleChange}
          accept={
            limits?.allowedExtensions
              ?.map((e) => `.${e.replace(/^\./, '')}`)
              .join(',') ?? undefined
          }
          aria-invalid={borderError || undefined}
          aria-describedby={
            [serverErrorId, showClientError && id ? `${id}-client-error` : '']
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
        {loading ? 'Загрузка…' : 'Выберите файл'}
      </InputLabel>

      {hasServerError && serverErrorId ? (
        <ServerErrorText id={serverErrorId} role="alert">
          {serverError}
        </ServerErrorText>
      ) : null}
      {showClientError && id ? (
        <ErrorText id={`${id}-client-error`} role="alert">
          {error}
        </ErrorText>
      ) : null}
      {showClientError && !id ? <ErrorText role="alert">{error}</ErrorText> : null}

      {showPreview && displayFile && (
        <Preview>
          {isImage && imageSrc ? (
            <PreviewImage
              src={imageSrc}
              alt={displayFile.filename ?? 'Превью'}
            />
          ) : null}
          <PreviewInfo>
            {!isImage && '📄 '}
            {displayFile.filename ?? 'Файл'}
            {displayFile.size != null && (
              <span style={{ opacity: 0.7, marginLeft: 8 }}>
                ({(displayFile.size / 1024).toFixed(1)} KB)
              </span>
            )}
            {displayFile.description != null && displayFile.description !== '' && (
              <div style={{ opacity: 0.75, marginTop: 4, fontSize: 12 }}>
                {displayFile.description}
              </div>
            )}
          </PreviewInfo>
        </Preview>
      )}
    </Wrapper>
  );
}
